import React from "react";
import { Link } from "react-router-dom";
import { FileText, Download, Lock, CheckCircle2, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import Seo from "../components/Seo";
import { PageHeader } from "../components/sections/Shared";
import { useAuth } from "../context/AuthContext";
import { SOPS } from "../data/compliance";
import { useToast } from "../hooks/use-toast";

function buildSopDocument(sop) {
  const lines = [
    "OBLED Corporation d/b/a 518 Courier",
    "INTERNAL SOP SUMMARY — CONFIDENTIAL",
    "",
    sop.title,
    "=".repeat(sop.title.length),
    "",
    sop.summary,
    "",
    "Key procedure points:",
    ...sop.points.map((p, i) => `  ${i + 1}. ${p}`),
    "",
    "This document is an internal SOP summary provided to authorized customers,",
    "drivers and administrators. It is confidential and must not be redistributed.",
    "",
    `Generated: ${new Date().toLocaleString()}`
  ];
  return lines.join("\n");
}

export default function Sops() {
  const { user } = useAuth();
  const { toast } = useToast();

  const download = (sop) => {
    if (!user) return;
    const blob = new Blob([buildSopDocument(sop)], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `518-Courier-SOP-${sop.slug}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: "SOP downloaded", description: `${sop.title} (internal summary) saved.` });
  };

  return (
    <>
      <Seo
        title="Standard Operating Procedures | 518 Courier"
        description="Public summaries of 518 Courier Standard Operating Procedures: chain-of-custody, proof-of-delivery, medical handling, temperature control, dry ice, vehicle inspection, driver qualification and incident reporting. Full documents available to authorized users."
        path="/sops"
      />
      <PageHeader
        eyebrow="Standard Operating Procedures"
        title="How we operate,"
        accent="documented."
        description="Public summaries of the procedures that guide our courier and medical operations. Full SOP documents are available to authorized customers, drivers and administrators."
        image="https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      <section className="py-14 md:py-20 bg-white">
        <div className="container-518">
          {!user && (
            <div className="mb-8 flex items-start gap-3 p-5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800" data-testid="sops-auth-notice">
              <Lock className="w-5 h-5 mt-0.5 shrink-0" />
              <div className="text-sm">
                The complete SOP documents are not publicly accessible. <Link to="/login" className="font-bold underline">Sign in</Link> as a customer, driver or admin to download the full internal SOP summaries.
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-5">
            {SOPS.map((sop) => (
              <div key={sop.slug} className="card-lift bg-white rounded-2xl border border-slate-200 p-6 flex flex-col" data-testid={`sop-card-${sop.slug}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-[#0f2847] text-white flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <h2 className="font-display font-bold text-[#0f2847] text-lg leading-tight">{sop.title}</h2>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">{sop.summary}</p>
                <ul className="space-y-2 mb-5">
                  {sop.points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-[#f59321] mt-0.5 shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-2">
                  {user ? (
                    <Button
                      onClick={() => download(sop)}
                      data-testid={`sop-download-${sop.slug}`}
                      className="bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-11 px-5 font-semibold"
                    >
                      <Download className="w-4 h-4 mr-2" /> Download full SOP
                    </Button>
                  ) : (
                    <Link to="/login">
                      <Button variant="outline" data-testid={`sop-locked-${sop.slug}`} className="border-2 border-slate-300 text-[#0f2847] hover:border-[#f59321] hover:text-[#f59321] rounded-full h-11 px-5 font-semibold bg-transparent">
                        <Lock className="w-4 h-4 mr-2" /> Sign in to access
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 flex items-start gap-3 p-5 rounded-2xl bg-slate-50 border border-slate-200 text-sm text-slate-600" data-testid="sops-footer-note">
            <ShieldCheck className="w-5 h-5 text-[#f59321] mt-0.5 shrink-0" />
            <span>Full SOP documents are internal summaries provided to authorized users only. For a formal policy pack, Business Associate Agreement or certificate of insurance, <Link to="/contact" className="text-[#f59321] font-semibold hover:underline">contact us</Link>.</span>
          </div>
        </div>
      </section>
    </>
  );
}
